import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PlaceAddress } from '@/features/google-location'
import {
  LEGAL_FORMS,
  RestaurantLogoField,
  TIMEZONE_OPTIONS
} from '@/features/restaurant-settings'
import { FormFieldConfig } from '@/shared/components/DynamicForm/DynamicForm'
import OpeningHoursField from '../components/OpeningHoursField'
import GoogleLocationBlock, {
  GooglePlaceDraft,
  GoogleReviewsBlock
} from '../sections/GoogleLocationBlocks'

/*
  A pick's address goes into the form like a typed edit, to be checked and
  kept by Save with the rest. A part Google has none of, such as the street of
  a village, leaves what the owner typed. Checked again at once after a Save
  was refused, so an error it fixes goes away.
*/
const fillAddress = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>,
  found: PlaceAddress
) =>
  Object.entries(found)
    .filter(([, value]) => value !== '')
    .forEach(([part, value]) =>
      form.setValue(`address.${part}`, value, {
        shouldDirty: true,
        shouldValidate: form.formState.isSubmitted
      })
    )

/**
 * Settings > Restaurant as one DynamicForm, in the order a guest meets the
 * venue: its logo and name, its place on Google and the address that fills
 * in, how to reach it and when it is open. Its Google reviews follow, and who
 * it is legally comes last, as nobody but an invoice or a payout reads it.
 *
 * Field names are paths into the settings document (`address.street`), so
 * the form's values are the document and its schema checks them as one. The
 * groups are only layout and have no name, since DynamicForm puts a group's
 * name in front of its fields'.
 * `tagline` and `description` are typed in `contentLanguage`.
 */
export const useRestaurantFormConfig = (
  contentLanguage: string,
  timezone: string,
  placeDraft: GooglePlaceDraft | null,
  onPlaceDraftChange: (draft: GooglePlaceDraft | null) => void
): FormFieldConfig[] => {
  const { t } = useTranslation()

  // The stored zone stays pickable when it is not one of the usual ones, as
  // a browser's own can be.
  const timezoneOptions = [
    ...new Set<string>([...TIMEZONE_OPTIONS, timezone].filter(Boolean))
  ].map(zone => ({ label: zone, value: zone }))

  const legalFormOptions = LEGAL_FORMS.map(form => ({
    label: t(`RESTAURANT_SETTINGS.VENUE.LEGAL_FORM.${form.toUpperCase()}`),
    value: form
  }))

  return [
    {
      name: '',
      type: 'input-group',
      heading: t('RESTAURANT_SETTINGS.TABS.VENUE'),
      inputGroupDirection: 'column',
      inputs: [
        {
          // Top-aligned with the logo, as fields are everywhere else. Three
          // lines of description keep the pair within the logo's height; a
          // fourth would run past its foot.
          name: '',
          type: 'input-group',
          alignItems: 'flex-start',
          inputs: [
            {
              // An upload of its own, saved as soon as it is cropped.
              name: 'logo',
              type: 'custom',
              flexGrow: 0,
              render: () => <RestaurantLogoField />
            },
            {
              name: '',
              type: 'input-group',
              inputGroupDirection: 'column',
              inputs: [
                {
                  name: 'identity.name',
                  type: 'text',
                  label: t('RESTAURANT_SETTINGS.VENUE.NAME'),
                  placeholder: t('RESTAURANT_SETTINGS.VENUE.NAME_PLACEHOLDER'),
                  focused: true,
                  required: true
                },
                {
                  name: `identity.description.${contentLanguage}`,
                  type: 'text-area',
                  lines: 3,
                  label: t('RESTAURANT_SETTINGS.VENUE.DESCRIPTION'),
                  placeholder: t(
                    'RESTAURANT_SETTINGS.VENUE.DESCRIPTION_PLACEHOLDER'
                  ),
                  focused: true
                }
              ]
            }
          ]
        },
        {
          name: `identity.tagline.${contentLanguage}`,
          type: 'text',
          label: t('RESTAURANT_SETTINGS.VENUE.TAGLINE'),
          placeholder: t('RESTAURANT_SETTINGS.VENUE.TAGLINE_PLACEHOLDER'),
          focused: true
        }
      ]
    },
    {
      // Above the address, which a pick fills in. The pick is a draft the
      // section holds, kept by Save with the address.
      name: 'googleLocation',
      type: 'custom',
      render: form => (
        <GoogleLocationBlock
          draft={placeDraft}
          onDraftChange={onPlaceDraftChange}
          onAddressFound={found => fillAddress(form, found)}
        />
      )
    },
    {
      name: '',
      type: 'input-group',
      heading: t('RESTAURANT_SETTINGS.TABS.ADDRESS'),
      gridColumns: 4,
      inputs: [
        {
          name: 'address.street',
          type: 'text',
          span: 2,
          label: t('RESTAURANT_SETTINGS.ADDRESS.STREET'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.STREET_PLACEHOLDER'),
          focused: true,
          required: true
        },
        {
          name: 'address.buildingNumber',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.ADDRESS.BUILDING'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.BUILDING_PLACEHOLDER'),
          focused: true,
          required: true
        },
        {
          name: 'address.unitNumber',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.ADDRESS.UNIT'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.UNIT_PLACEHOLDER'),
          focused: true
        },
        {
          name: 'address.postalCode',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.ADDRESS.POSTAL_CODE'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.POSTAL_CODE_PLACEHOLDER'),
          focused: true,
          required: true
        },
        {
          name: 'address.city',
          type: 'text',
          span: 3,
          label: t('RESTAURANT_SETTINGS.ADDRESS.CITY'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.CITY_PLACEHOLDER'),
          focused: true,
          required: true
        },
        {
          name: 'address.deliveryNotes',
          type: 'text-area',
          lines: 2,
          span: 'all',
          label: t('RESTAURANT_SETTINGS.ADDRESS.NOTES'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.NOTES_PLACEHOLDER'),
          focused: true
        },
        {
          // The opening hours are read in it.
          name: 'address.timezone',
          type: 'select',
          span: 2,
          label: t('RESTAURANT_SETTINGS.ADDRESS.TIMEZONE'),
          placeholder: t('RESTAURANT_SETTINGS.ADDRESS.TIMEZONE'),
          options: timezoneOptions,
          focused: true,
          required: true,
          hideClearIcon: true
        }
      ]
    },
    {
      name: '',
      type: 'input-group',
      heading: t('RESTAURANT_SETTINGS.TABS.CONTACT'),
      inputGroupDirection: 'column',
      inputs: [
        {
          name: '',
          type: 'input-group',
          gridColumns: 2,
          inputs: [
            {
              name: 'contact.phone',
              type: 'text',
              label: t('RESTAURANT_SETTINGS.CONTACT.PHONE'),
              placeholder: t('RESTAURANT_SETTINGS.CONTACT.PHONE_PLACEHOLDER'),
              focused: true,
              required: true
            },
            {
              name: 'contact.email',
              type: 'text',
              label: t('RESTAURANT_SETTINGS.CONTACT.EMAIL'),
              placeholder: t('RESTAURANT_SETTINGS.CONTACT.EMAIL_PLACEHOLDER'),
              focused: true
            }
          ]
        },
        {
          name: 'contact.websiteUrl',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.CONTACT.WEBSITE'),
          placeholder: t('RESTAURANT_SETTINGS.CONTACT.WEBSITE_PLACEHOLDER'),
          focused: true
        },
        {
          name: '',
          type: 'input-group',
          heading: t('RESTAURANT_SETTINGS.CONTACT.SOCIAL'),
          gridColumns: 2,
          inputs: [
            {
              name: 'contact.facebookUrl',
              type: 'text',
              label: t('RESTAURANT_SETTINGS.CONTACT.FACEBOOK'),
              placeholder: t('RESTAURANT_SETTINGS.CONTACT.FACEBOOK_PLACEHOLDER'),
              focused: true
            },
            {
              name: 'contact.instagramUrl',
              type: 'text',
              label: t('RESTAURANT_SETTINGS.CONTACT.INSTAGRAM'),
              placeholder: t(
                'RESTAURANT_SETTINGS.CONTACT.INSTAGRAM_PLACEHOLDER'
              ),
              focused: true
            }
          ]
        }
      ]
    },
    {
      name: '',
      type: 'input-group',
      heading: t('RESTAURANT_SETTINGS.TABS.HOURS'),
      inputGroupDirection: 'column',
      inputs: [
        {
          name: 'hours',
          type: 'custom',
          render: form => (
            <OpeningHoursField control={form.control} name="hours" />
          )
        }
      ]
    },
    {
      // Only for a connected place; renders nothing, and takes no room,
      // otherwise.
      name: 'googleReviews',
      type: 'custom',
      render: () => <GoogleReviewsBlock />
    },
    {
      name: '',
      type: 'input-group',
      heading: t('RESTAURANT_SETTINGS.VENUE.LEGAL'),
      gridColumns: 4,
      inputs: [
        {
          name: 'identity.legalName',
          type: 'text',
          span: 'all',
          label: t('RESTAURANT_SETTINGS.VENUE.LEGAL_NAME'),
          placeholder: t('RESTAURANT_SETTINGS.VENUE.LEGAL_NAME_PLACEHOLDER'),
          hint: t('RESTAURANT_SETTINGS.VENUE.LEGAL_NAME_HINT'),
          focused: true
        },
        {
          name: 'identity.legalForm',
          type: 'select',
          span: 2,
          label: t('RESTAURANT_SETTINGS.VENUE.LEGAL_FORM.LABEL'),
          placeholder: t('RESTAURANT_SETTINGS.VENUE.LEGAL_FORM.PLACEHOLDER'),
          options: legalFormOptions,
          focused: true
        },
        {
          name: 'identity.nip',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.VENUE.NIP'),
          placeholder: t('RESTAURANT_SETTINGS.VENUE.NIP_PLACEHOLDER'),
          focused: true
        },
        {
          name: 'identity.regon',
          type: 'text',
          label: t('RESTAURANT_SETTINGS.VENUE.REGON'),
          placeholder: t('RESTAURANT_SETTINGS.VENUE.REGON_PLACEHOLDER'),
          focused: true
        }
      ]
    }
  ]
}

export default useRestaurantFormConfig
